import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthCommsDistributionsTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { DistributionPreviewResult, DistributionRun, GrowthCommsDistributionService } from './growth-comms-distribution.service';

@Component({
  standalone: true,
  selector: 'ain-growth-comms-distribution',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, AinFormFieldComponent,
    GrowthSegmentSelectorComponent, PartySelectorComponent, FormsModule, TableModule,
    ButtonModule, InputTextModule, TextareaModule, SelectModule, AinCanAccessDirective
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.distribution.title')" [description]="t('growth.distribution.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.distribution.help.title'" [descriptionKey]="'growth.distribution.help.description'"
        [bulletKeys]="['growth.distribution.help.bullets.segment','growth.distribution.help.bullets.preview','growth.distribution.help.bullets.live']" />

      <section class="card">
        <h3>{{ t('growth.distribution.sections.createRun') }}</h3>
        <div class="grid">
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.segment')"><ain-growth-segment-selector [(ngModel)]="createForm.segmentId" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="createForm.channelKey" appendTo="body" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.purpose')"><p-select [options]="purposeOptions" optionLabel="label" optionValue="value" [(ngModel)]="createForm.purposeKey" appendTo="body" /></ain-form-field>
          <ain-form-field [label]="t('growth.marketing.campaigns.fields.locale')"><input pInputText [(ngModel)]="createForm.locale" /></ain-form-field>
          <ain-form-field [label]="t('notifications.templates.columns.key')"><input pInputText [(ngModel)]="createForm.templateKey" /></ain-form-field>
          <ain-form-field [label]="t('notifications.messages.columns.subject')"><input pInputText [(ngModel)]="createForm.subject" /></ain-form-field>
        </div>
        <ain-form-field [label]="t('notifications.messageDetail.sections.renderedBody')"><textarea pInputTextarea rows="5" [(ngModel)]="createForm.body"></textarea></ain-form-field>
        <div class="toolbar">
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.distribution.manage'" pButton type="button" [label]="t('growth.distribution.actions.snapshotAndCreate')" [loading]="creating()" [disabled]="!createForm.segmentId" (click)="createRun()"></button>
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.test'" pButton type="button" severity="secondary" [label]="t('growth.distribution.actions.testSend')" (click)="openTestSend()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
      </section>

      @if (preview()) {
        <section class="card preview-card">
          <h4>{{ t('growth.distribution.sections.preview') }}</h4>
          <div class="counters">
            <span>{{ t('growth.distribution.counters.total') }}: {{ preview()!.totalCount }}</span>
            <span>{{ t('growth.distribution.counters.eligible') }}: {{ preview()!.eligibleCount }}</span>
            <span>{{ t('growth.distribution.counters.suppressed') }}: {{ preview()!.suppressedCount }}</span>
          </div>
          @for (entry of reasonEntries(preview()!); track entry.key) {
            <div>{{ entry.key }}: {{ entry.value }}</div>
          }
        </section>
      }

      <section class="card">
        <div class="toolbar">
          <ain-form-field [label]="t('growth.publishOperations.filters.status')">
            <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="statusFilter" [showClear]="true" appendTo="body" />
          </ain-form-field>
          <button pButton type="button" [label]="t('catalog.action.refresh')" (click)="loadRuns()"></button>
        </div>
        @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
        @else {
          <p-table [value]="runs()" [lazy]="true" [paginator]="true" [rows]="15" [totalRecords]="runTotal()" (onLazyLoad)="onRunsLazyLoad($event)">
            <ng-template pTemplate="header"><tr><th>{{ t('notifications.messages.columns.subject') }}</th><th>{{ t('growth.marketing.campaigns.fields.channel') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('growth.distribution.counters.eligible') }}</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><button type="button" class="link-btn" (click)="openRun(row.id)">{{ row.subject }}</button></td>
                <td>{{ row.channelKey }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.eligibleCount }}/{{ row.totalCount }}</td>
                <td><button pButton size="small" [label]="t('common.view')" (click)="openRun(row.id)"></button></td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      @if (testSendVisible()) {
        <section class="card">
          <h4>{{ t('growth.distribution.actions.testSend') }}</h4>
          <ain-form-field [label]="t('notifications.policy.fields.party')"><ain-party-selector [(ngModel)]="testSendForm.partyId" /></ain-form-field>
          <button ainCanAccess [ainCanAccessPermission]="'growth.comms.test'" pButton type="button" [label]="t('growth.distribution.actions.sendTest')" [disabled]="!testSendForm.partyId" [loading]="testSending()" (click)="sendTest()"></button>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:12px}
    .grid{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(200px,1fr))}
    .toolbar{display:flex;gap:8px;align-items:end;flex-wrap:wrap}.counters{display:flex;gap:12px;flex-wrap:wrap}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}.live-badge{font-size:0.85rem}
  `]
})
export class GrowthCommsDistributionComponent implements OnInit {
  private readonly api = inject(GrowthCommsDistributionService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(false);
  readonly creating = signal(false);
  readonly testSending = signal(false);
  readonly testSendVisible = signal(false);
  readonly runs = signal<DistributionRun[]>([]);
  readonly runTotal = signal(0);
  readonly preview = signal<DistributionPreviewResult | null>(null);

  statusFilter: string | null = null;
  createForm = { segmentId: null as string | null, channelKey: 'email', purposeKey: 'marketing', locale: 'fa-IR', templateKey: '', subject: '', body: '' };
  testSendForm = { partyId: null as string | null };

  readonly channelOptions = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' }
  ];
  readonly purposeOptions = [
    { label: 'Marketing', value: 'marketing' },
    { label: 'Transactional', value: 'transactional' },
    { label: 'Service', value: 'service' }
  ];
  readonly statusOptions = [
    { label: 'Draft', value: 'Draft' },
    { label: 'Previewed', value: 'Previewed' },
    { label: 'PendingApproval', value: 'PendingApproval' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Dispatching', value: 'Dispatching' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' }
  ];

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap;
    if (q.get('segmentId')) this.createForm.segmentId = q.get('segmentId');
    if (q.get('contentAssetId')) this.createForm.body = this.t('growth.distribution.prefill.fromContent');
    if (q.get('channelKey')) this.createForm.channelKey = q.get('channelKey')!;

    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthCommsDistributionChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRuns(false));
    void this.setupLiveSubscription();
    this.loadRuns(true);
  }

  t(key: string): string { return this.i18n.translate(key); }
  reasonEntries(preview: DistributionPreviewResult): Array<{ key: string; value: number }> {
    return Object.entries(preview.reasonCounts).map(([key, value]) => ({ key, value }));
  }

  loadRuns(showLoading = true, skip = 0, take = 15): void {
    if (showLoading) this.loading.set(true);
    this.api.listRuns(this.statusFilter ?? undefined, skip, take).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: page => { this.runs.set(page.items); this.runTotal.set(page.totalCount); }
    });
  }

  onRunsLazyLoad(event: TableLazyLoadEvent): void {
    this.loadRuns(false, event.first ?? 0, event.rows ?? 15);
  }

  createRun(): void {
    if (!this.canAccess('growth.comms.distribution.manage')) return;
    if (!this.createForm.segmentId) return;
    this.creating.set(true);
    const idem = `dist-${Date.now()}`;
    this.api.createSnapshot({ segmentId: this.createForm.segmentId, idempotencyKey: `snap-${idem}` }).pipe(
      finalize(() => this.creating.set(false))
    ).subscribe({
      next: snapshot => {
        this.api.createRun({
          audienceSnapshotId: snapshot.id,
          contentAssetId: this.route.snapshot.queryParamMap.get('contentAssetId'),
          campaignId: this.route.snapshot.queryParamMap.get('campaignId'),
          channelKey: this.createForm.channelKey,
          purposeKey: this.createForm.purposeKey,
          locale: this.createForm.locale,
          templateKey: this.createForm.templateKey || null,
          subject: this.createForm.subject,
          body: this.createForm.body,
          idempotencyKey: idem
        }).subscribe({
          next: run => {
            this.api.previewRun(run.id).subscribe(preview => this.preview.set(preview));
            this.loadRuns(false);
            void this.router.navigate(['/growth/comms/distributions', run.id]);
          }
        });
      }
    });
  }

  openTestSend(): void {
    if (!this.canAccess('growth.comms.test')) return;
    this.testSendVisible.set(true);
  }

  sendTest(): void {
    if (!this.canAccess('growth.comms.test')) return;
    if (!this.testSendForm.partyId) return;
    this.testSending.set(true);
    this.api.testSend({
      partyId: this.testSendForm.partyId,
      channelKey: this.createForm.channelKey,
      purposeKey: this.createForm.purposeKey,
      subject: this.createForm.subject || this.t('growth.distribution.test.subject'),
      body: this.createForm.body || this.t('growth.distribution.test.body'),
      templateKey: this.createForm.templateKey || null,
      idempotencyKey: `test-${Date.now()}`
    }).pipe(finalize(() => this.testSending.set(false))).subscribe();
  }

  openRun(runId: string): void { void this.router.navigate(['/growth/comms/distributions', runId]); }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    await this.liveUpdateService.subscribe(growthCommsDistributionsTopic(tenantId));
  }

  private canAccess(permission: string): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === permission);
  }
}
